<template>
    <RgGlobalNavbar />
    <Toast />
    <ConfirmDialog />
    <InputDialog />
    <DynamicDialog />
    <template v-if="loading">
        <MLoading
            :style="{
                cursor: 'wait',
                background: loadingBackground,
            }"
            :strokeWidth="5">
            {{ loadingText }}
        </MLoading>
    </template>
    <template v-else>
        <RouterView />
    </template>
    <MCookieBanner />
</template>
<script lang="ts" setup>
import ConfirmDialog from "primevue/confirmdialog";
import { computed, onMounted, ref } from "vue";
import { LocationQueryValue, useRoute, useRouter } from "vue-router";
import {
    InputDialog,
    MLoading,
    setupNotificationStore,
    useServerApi,
    useSessionManager,
    RgGlobalNavbar,
    useLocalizationStore,
    LocaleMessage,
    MCookieBanner,
    getDefaultPallete,
    getDefaultColor,
    applyDefaultLayout,
    setPatientListBackground,
    useAuth,
    AppLanguage,
    normalizeAppLanguage,
} from "@xlabmedical/medic-web-framework";
import { WebCADModule } from "@xlabmedical/medic-api";
import { useTitle } from "@vueuse/core";
import Toast from "primevue/toast";
import DynamicDialog from "primevue/dynamicdialog";
import { setupSession } from "@/util/SetupSession";
import { useI18n, VueMessageType } from "vue-i18n";
import { updateSurfacePalette } from "@primevue/themes";
import { updatePrimaryPalette } from "@primeuix/styled";
import { NamedRoutes, redirectToLogin } from "./router/WebRgRouter";
import { useWebsocketManager } from "@/plugins/WebRgManagerPlugin";
import { usePatientsStore } from "@/stores/PatientsStore";

const t = useI18n();

const router = useRouter();
const loading = ref(true);
const loadingText = "Checking session";
const route = useRoute();
const title = useTitle();
const auth = useAuth();
const sessionManager = useSessionManager();
const api = useServerApi();
const localizationStore = useLocalizationStore();
const wsManager = useWebsocketManager();
const patientsStore = usePatientsStore();

const loadingBackground = computed(() => {
    return "var(--webcad-main-bg)";
});

const clearPatientProjectSelection = () => {
    patientsStore.clearSelectedPatientsAndProjects();
    patientsStore.patients = [];
    patientsStore.projectsModel = [];
    patientsStore.legacyProjectsModel = [];
};

onBeforeMount(async () => {
    setupNotificationStore();
});

onMounted(async () => {
    applyDefaultLayout();
    setPatientListBackground();
    await router.isReady();

    if (route.name === NamedRoutes.UnsupportedBrowser) {
        loading.value = false;
        return;
    }

    const sessionOverride = route.query.overrideSession as LocationQueryValue;
    const loginToken = route.query.accessToken as LocationQueryValue;

    if (loginToken) {
        if (auth.isAuthenticated()) {
            clearPatientProjectSelection();
            await auth.logout();
        }

        await auth.loginWithToken(loginToken);
        await router.replace({ query: undefined });
    }

    if (sessionOverride) {
        auth.setExternalSession(sessionOverride);
    }

    updateSurfacePalette(getDefaultPallete());
    updatePrimaryPalette(getDefaultColor().palette);

    if (auth.isAuthenticated() && (await auth.checkSession(WebCADModule.PLANNER))) {
        const user = auth.getUser();
        title.value = "RG WEB";
        await setupSession(auth, sessionManager, api, wsManager);
        await patientsStore.ensureVendorName();
        await setupLocalization(user?.lang ?? AppLanguage.EN);

        if (route.matched.length === 0 || route.path === "/") {
            await router.push("/patients");
        }
    } else {
        await redirectToLogin();
    }

    loading.value = false;
});

const setupLocalization = async (language: string) => {
    const resolvedLanguage = normalizeAppLanguage(language);
    const messages = await localizationStore.getWebcadTranslations(resolvedLanguage);

    if (messages) {
        t.setLocaleMessage(resolvedLanguage, messages as unknown as LocaleMessage<VueMessageType>);
        t.locale.value = resolvedLanguage;
        localizationStore.setAppLanguage(resolvedLanguage);
    }
};
</script>
<style>
html,
body {
    height: 100%;
}

html.rg-smartphone-scroll-fix,
body.rg-smartphone-scroll-fix {
    height: auto;
    min-height: 100%;
}

body.rg-smartphone-scroll-fix {
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
}

body.rg-smartphone-scroll-fix #app {
    height: auto;
    min-height: calc(100svh - 80px);
    min-height: calc(100dvh - 80px);
}

#app {
    font-family: Roboto, Avenir, Helvetica, Arial, sans-serif;
    font-weight: 400;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    height: calc(100vh - 80px);
    cursor: default;
}
</style>
